import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { updateUser, type UserSummary } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

type Props = {
  user: UserSummary | null;
  onClose: () => void;
};

export function EditUserModal({ user, onClose }: Props) {
  const [displayName, setDisplayName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [newPassword, setNewPassword] = useState("");
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user) {
      setDisplayName(user.display_name);
      setIsAdmin(user.is_admin);
      setIsActive(user.is_active);
      setNewPassword("");
    }
  }, [user]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!user) throw new Error("no user");
      const payload: Parameters<typeof updateUser>[1] = {
        display_name: displayName,
        is_admin: isAdmin,
        is_active: isActive,
      };
      if (newPassword.length > 0) payload.password = newPassword;
      return updateUser(user.id, payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success(
        newPassword.length > 0
          ? "User aktualisiert — alle aktiven Sessions wurden widerrufen."
          : "User aktualisiert",
      );
      onClose();
    },
    onError: (err) => {
      const ax = err as AxiosError<{ message?: string }>;
      toast.error(ax.response?.data?.message ?? "Update fehlgeschlagen");
    },
  });

  const passwordTooShort = newPassword.length > 0 && newPassword.length < 8;

  return (
    <Dialog open={user !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>User bearbeiten</DialogTitle>
          <DialogDescription>
            {user ? user.email : ""} — Änderungen sind sofort aktiv.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="dn">Anzeigename</Label>
            <Input
              id="dn"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.target.checked)}
            />
            Admin
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Aktiv
          </label>
          <div className="space-y-1.5 rounded-md border border-destructive/40 bg-destructive/5 p-3">
            <Label htmlFor="pw">Neues Passwort (optional)</Label>
            <Input
              id="pw"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Leer lassen, um nichts zu ändern"
            />
            <p className="text-xs text-destructive">
              Achtung: Beim Setzen werden ALLE aktiven Sessions dieses Users
              widerrufen — er muss sich neu einloggen.
            </p>
            {passwordTooShort && (
              <p className="text-xs text-destructive">
                Mindestens 8 Zeichen.
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={passwordTooShort || mutation.isPending}
          >
            {mutation.isPending ? "Speichere…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
